/**
 * The checks a campaign condition step can make, as the builder stores them in a conditional node's
 * `conditionalValue` (`{ property, value, title, currentType }`). One table for the builder's
 * condition picker (`buildConditionalValue`) and the sequence converter (PREC-4258), so a condition
 * written over MCP reads exactly like one picked in the builder.
 */

/** The `currentType` a condition node carries, one per entry of the builder's condition picker. */
export type CampaignConditionKind =
	| "leadHasEmail"
	| "linkedinInviteNode"
	| "linkedinConnected"
	| "leadHasLinkedin"
	| "emailOpened"
	| "emailClicked"
	| "leadPriority";

export interface CampaignConditionLabel {
	label: string;
	value: string;
}

export const CAMPAIGN_CONDITION_LABELS: Readonly<Record<CampaignConditionKind, CampaignConditionLabel>> = {
	leadHasEmail: { label: "Lead has email", value: "hasEmail" },
	linkedinInviteNode: { label: "Lead accepted invitation", value: "accepted" },
	linkedinConnected: { label: "Lead is connected", value: "accepted" },
	leadHasLinkedin: { label: "Lead has linkedin", value: "hasLinkedin" },
	emailOpened: { label: "E-Mail has been opened", value: "opened" },
	emailClicked: { label: "E-Mail has been clicked", value: "clicked" },
	leadPriority: { label: "Lead Priority", value: "priority" },
};

/** A condition node's stored value. */
export interface CampaignConditionalValue {
	property: string;
	value: string;
	title: string;
	currentType?: string;
}

/** The value a condition of this kind is stored with; a plain "conditional" starts as "has email". */
export function campaignConditionalValue(kind?: string): CampaignConditionalValue {
	const { label, value } =
		CAMPAIGN_CONDITION_LABELS[kind as CampaignConditionKind] ?? CAMPAIGN_CONDITION_LABELS.leadHasEmail;

	return { property: label, value, title: label, currentType: kind };
}
